import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { AlertCircleIcon } from '@/components/icons/AlertCircleIcon';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { CheckCircleIcon } from '@/components/icons/CheckCircleIcon';
import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { User01FilledIcon } from '@/components/icons/User01FilledIcon';
import { COLORS, GRAY } from '@/styles/tokens/colors';

export type ProfileSetupStatus = 'processing' | 'review' | 'ready' | 'unknown';
export type PresentationSetupStatus =
  | 'processing'
  | 'ready'
  | 'failed'
  | 'not-created';

function StatusRow({
  title,
  description,
  icon,
  children,
  wrap,
  complete = false,
}: {
  title: string;
  description: ReactNode;
  icon: ReactNode;
  children: ReactNode;
  wrap: boolean;
  complete?: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      p={2}
      flexWrap={wrap ? 'wrap' : 'nowrap'}
      useFlexGap={wrap}
    >
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1,
          bgcolor: GRAY[100],
          color: complete ? COLORS.TEXT.PRIMARY : COLORS.TEXT.SECONDARY,
          display: 'flex',
        }}
      >
        {icon}
      </Box>
      <Stack flex="1 1 160px" minWidth={0} spacing={0.5}>
        <Typography variant="title-xs">{title}</Typography>
        <Typography
          component="div"
          variant="body-xs"
          sx={{ color: COLORS.TEXT.SECONDARY }}
        >
          {description}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
}

const profileDescriptions: Record<ProfileSetupStatus, string> = {
  processing: 'Saving verified data and preparing suggestions for your review',
  review: 'Suggestions ready for review',
  ready: 'Data has been saved and the client profile is complete',
  unknown: 'Client profile is available',
};
const presentationDescriptions: Record<PresentationSetupStatus, string> = {
  processing: 'Preparing your presentation',
  ready: 'Built from your client profile',
  'not-created': 'Built from your client profile',
  failed: 'Latest presentation generation failed',
};

export function PresentationFailureIndicator() {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" role="status">
      <AlertCircleIcon size={16} color={COLORS.FUNCTIONAL.WARNING[600]} />
      <Typography variant="body-xs">
        {presentationDescriptions.failed}
      </Typography>
    </Stack>
  );
}

export interface DeliverablesCardProps {
  profile: ProfileSetupStatus;
  suggestionCount?: number;
  onReviewSuggestions?: () => void;
  presentation?: PresentationSetupStatus;
  presentationCount?: number;
  onViewPresentations: () => void;
  onCreatePresentation?: () => void;
}

export function DeliverablesCard({
  profile,
  suggestionCount,
  onReviewSuggestions,
  presentation,
  presentationCount = 1,
  onViewPresentations,
  onCreatePresentation,
}: DeliverablesCardProps) {
  let presentationDescription: ReactNode = presentation
    ? presentationDescriptions[presentation]
    : undefined;
  if (presentation === 'failed') {
    presentationDescription = <PresentationFailureIndicator />;
  } else if (presentation === 'processing' && presentationCount > 1) {
    presentationDescription = 'Preparing your presentations';
  }
  return (
    <Stack
      divider={<Box sx={{ borderBottom: `1px solid ${GRAY[200]}` }} />}
      sx={{
        border: `1px solid ${GRAY[200]}`,
        borderRadius: 1,
        bgcolor: COLORS.PRIMITIVES.WHITE,
      }}
    >
      <StatusRow
        title="Client profile"
        description={profileDescriptions[profile]}
        icon={<User01FilledIcon size={16} />}
        complete={profile === 'ready' || profile === 'review'}
        wrap
      >
        {profile === 'processing' && (
          <CircularProgress
            size={20}
            aria-label="Preparing client profile"
            sx={{ color: GRAY[700], flexShrink: 0 }}
          />
        )}
        {profile === 'ready' && (
          <Box role="img" aria-label="Client profile complete">
            <CheckCircleIcon size={20} color={COLORS.FUNCTIONAL.SUCCESS[600]} />
          </Box>
        )}
        {profile === 'review' && onReviewSuggestions && (
          <Button
            variant="primary"
            size="xs"
            endIcon={ArrowRightIcon}
            onClick={onReviewSuggestions}
          >
            Review {suggestionCount}{' '}
            {suggestionCount === 1 ? 'suggestion' : 'suggestions'}
          </Button>
        )}
      </StatusRow>
      {presentation && (
        <StatusRow
          title="Presentations"
          description={presentationDescription}
          icon={<PresentationChart01Icon size={16} />}
          complete={presentation === 'ready'}
          wrap
        >
          {presentation === 'processing' && (
            <CircularProgress
              size={20}
              aria-label={
                presentationCount > 1
                  ? 'Preparing presentations'
                  : 'Preparing presentation'
              }
              sx={{ color: GRAY[700], flexShrink: 0 }}
            />
          )}
          {(presentation === 'ready' || presentation === 'failed') && (
            <Button variant="secondary" size="xs" onClick={onViewPresentations}>
              View presentations
            </Button>
          )}
          {presentation === 'not-created' && onCreatePresentation && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onCreatePresentation}
            >
              Create presentation
            </Button>
          )}
        </StatusRow>
      )}
    </Stack>
  );
}
